import type { AgentDotState } from '../state/agentListStore';
import { useLongPress } from '../hooks/useLongPress';

interface SessionDotProps {
  tabId: string;
  dotState: AgentDotState;
  tabIndex: number;
  onSwitch: (sessionId: string) => void;
  /**
   * Long-press requests sleep. The parent shows a "Sleep this session?" confirm
   * dialog before committing — the dot is never slept by the press alone.
   */
  onRequestSleep: (sessionId: string) => void;
  /**
   * Optional presentational label shown next to the dot (P4a — active-only).
   * Only the active dot receives one; when present the dot + label are wrapped
   * in `.utb-dot-wrap`. When `onRename` is also provided (P4b — identity
   * migration), the label becomes the tap-to-rename affordance; otherwise it is
   * purely presentational and clicks fall through to the dot.
   */
  label?: string;
  /**
   * P4b — identity migration. When provided (active dot only), tapping the label
   * opens the rename dialog — this is the affordance that replaced the removed
   * top-header session name. The dot itself still switches / sleeps independently.
   */
  onRename?: () => void;
  /**
   * P4b — single-session identity. When false, long-press-to-sleep is disabled
   * (a lone session has nowhere to auto-advance to, so sleeping it is a footgun).
   * Defaults to true — multi-session behaviour is unchanged.
   */
  sleepable?: boolean;
  /**
   * Context window usage % for the active session (0–100).
   * Replaces the blank wider pill with a compact "17%" label.
   */
  contextPct?: number;
  /**
   * True when contextPct is a mid-turn PROJECTION (context-fullness Phase 0b) —
   * renders `~NN%` instead of `NN%` (display-only estimate, never a warning).
   */
  contextProjected?: boolean;
  /**
   * User's auto-save/reset threshold (default 71). Color tiers are computed
   * relative to this value: amber at 60% of threshold, red at 80% of threshold.
   * Example with threshold=51: amber ≥31%, red ≥41%.
   */
  resetThreshold?: number;
  /** Called when the active context-% dot is tapped — opens the ContextBar sheet. */
  onOpenContextBar?: () => void;
}

/**
 * A single session dot in the session-dots-overlay.
 * Extracted into its own component so useLongPress can be called at the component
 * level (hooks cannot be called inside .map() in the parent).
 *
 * Note: slept (`idle`) dots are never rendered here — the parent filters them out
 * via isDotHiddenFromStrip — so this component only ever sees visible states.
 */
export function SessionDot({
  tabId,
  dotState,
  tabIndex,
  onSwitch,
  onRequestSleep,
  label,
  onRename,
  sleepable = true,
  contextPct,
  contextProjected = false,
  resetThreshold,
  onOpenContextBar,
}: SessionDotProps) {
  // The active dot IS long-pressable now — sleeping the current chat is a
  // first-class action (it auto-advances to a neighbour). Only a session that
  // holds the live mic is excluded — and (P4b) a lone session, which has nowhere
  // to advance to, so the parent passes sleepable={false}.
  const canSleep = sleepable && dotState !== 'recording';

  const longPressHandlers = useLongPress(
    () => {
      if (!canSleep) return;
      onRequestSleep(tabId);
    },
    500,
  );

  const hasPct = dotState === 'active' && contextPct != null && contextPct > 0;

  // Color tiers relative to the user's reset threshold (default 71).
  // amber at 60% of threshold, red at 80% — warns proportionally to when reset fires.
  const thr = resetThreshold ?? 71;
  const amberAt = Math.round(thr * 0.6);
  const redAt   = Math.round(thr * 0.8);

  const handleClick = () => {
    if (dotState === 'active') {
      if (hasPct && onOpenContextBar) onOpenContextBar();
      return;
    }
    onSwitch(tabId);
  };

  const classNames = [
    'utb-dot',
    dotState !== 'default' ? `utb-dot--${dotState}` : '',
    hasPct ? 'utb-dot--has-pct' : '',
    hasPct && contextPct! < amberAt  ? 'utb-dot--ctx-green' : '',
    hasPct && contextPct! >= amberAt && contextPct! < redAt ? 'utb-dot--ctx-amber' : '',
    hasPct && contextPct! >= redAt   ? 'utb-dot--ctx-red' : '',
  ].filter(Boolean).join(' ');

  const dot = (
    <span
      class={classNames}
      role="button"
      tabIndex={0}
      aria-label={hasPct ? `Context: ${contextProjected ? '~' : ''}${contextPct}%${contextProjected ? ' (projected)' : ''} — tap for details` : `Switch to session ${tabIndex + 1}`}
      onClick={handleClick}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      {...(canSleep ? longPressHandlers : {})}
    >
      {hasPct && <span class="utb-dot-pct">{contextProjected ? '~' : ''}{contextPct}%</span>}
    </span>
  );

  // No label → render the bare dot exactly as before (non-active dots unchanged).
  if (!label) return dot;

  // Active-only label. P4b: when onRename is provided the label is the
  // tap-to-rename affordance (identity migrated down from the top header);
  // otherwise it stays purely presentational and clicks fall through to the dot.
  return (
    <span class="utb-dot-wrap">
      {dot}
      {onRename ? (
        <span
          class="utb-dot-label utb-dot-label--editable"
          role="button"
          tabIndex={0}
          title="Rename session"
          onClick={(e) => { e.stopPropagation(); onRename(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onRename(); } }}
        >{label}</span>
      ) : (
        <span class="utb-dot-label">{label}</span>
      )}
    </span>
  );
}
